import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/Dialogs';
import { useFeatureFlags, type FeatureFlagScope } from '@/state/useFeatureFlags';

const FIELD_INPUT = 'border-line bg-field text-foreground h-10 rounded-lg text-body';
const FIELD_TEXTAREA = 'border-line bg-field text-foreground rounded-lg text-body';

const SCOPE_LABELS: Record<FeatureFlagScope, string> = {
  dashboard: 'Dashboard',
  runtime: 'Runtime',
  editor: 'Editor',
};

type CreateFeatureFlagModalProps = {
  open: boolean;
  scope: FeatureFlagScope;
  onClose: () => void;
};

export const CreateFeatureFlagModal = ({ open, scope, onClose }: CreateFeatureFlagModalProps) => {
  const navigate = useNavigate();
  const { createFlag } = useFeatureFlags();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onConfirm = async () => {
    setIsSubmitting(true);
    const flag = await createFlag({ name, description, scope });
    setIsSubmitting(false);
    setName('');
    setDescription('');
    onClose();
    navigate(`/admin/feature-flags/${flag.id}`);
  };

  return (
    <ConfirmDialog
      open={open}
      title={`Create Branch Flag (${SCOPE_LABELS[scope]})`}
      confirmLabel="Create"
      disabled={!name.trim() || isSubmitting}
      onConfirm={onConfirm}
      onCancel={onClose}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="feature-flag-name" className="micro-label block">
            Feature Name
          </label>
          <Input
            id="feature-flag-name"
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            placeholder="e.g. html-to-image-screenshots"
            className={FIELD_INPUT}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="feature-flag-description" className="micro-label block">
            Description
          </label>
          <Textarea
            id="feature-flag-description"
            value={description}
            onChange={(ev) => setDescription(ev.target.value)}
            placeholder="What does this feature do?"
            className={`${FIELD_TEXTAREA} min-h-[80px] resize-y`}
          />
        </div>
      </div>
    </ConfirmDialog>
  );
};
